import { type ApolloError, type ApolloQueryResult, useQuery } from '@apollo/client';
import { FD_MIMETYPE_SLUG, FD_NAME_SLUG, PROXY_FORMAT_SLUG } from 'src/constants';
import { buildFormatsQ, buildSharedFormatsQ } from 'src/dynamoQueries/queryHelpers';
import { getFirstNestedWithSaneDefault } from 'src/helpers/common';
import { useMetaQ } from './meta';

// Format data structure
interface FormatData {
  id: string;
  slug?: string;
  name?: string;
}

// Metadata field definition
interface MetadataFieldDefinition {
  slug: string;
  name?: string;
}

// Metadata value
interface MetadataValue {
  data?: {
    data?: string;
  };
}

// Metadata field
interface MetadataField {
  definition: MetadataFieldDefinition;
  values?: MetadataValue[];
}

// Asset metadata response
interface AssetMetaDataResponse {
  metadata?: MetadataField[];
  sharedMetadata?: MetadataField[];
}

// Proxy format response - dynamic key based on query
interface ProxyFormatResponse {
  [key: string]: FormatData[] | undefined;
}

// Format metadata response
interface FormatMetaDataResponse {
  metadata?: MetadataField[];
  sharedMetadata?: MetadataField[];
}

// Return type for useMediaQueries
interface UseMediaQueriesReturn {
  assetMetaData: AssetMetaDataResponse | undefined;
  assetMetaDataLoading: boolean;
  assetMetaDataError: ApolloError | undefined;
  proxyFormatData: ProxyFormatResponse | undefined;
  formatData: FormatMetaDataResponse | undefined;
  proxyFormat: FormatData;
  refetchAssetMetaData: () => Promise<ApolloQueryResult<AssetMetaDataResponse>>;
}

const useQueries = (itemId: string | undefined, isShared: boolean): UseMediaQueriesReturn => {
  const {
    data: assetMetaData,
    loading: assetMetaDataLoading,
    error: assetMetaDataError,
    refetch: refetchAssetMetaData,
  } = useMetaQ<AssetMetaDataResponse>(
    {
      skip: !itemId,
      variables: { globalId: itemId, definitionSlugIn: [FD_NAME_SLUG, 'title'] },
    },
    isShared,
  );

  const { data: proxyFormatData } = useQuery<ProxyFormatResponse>(isShared ? buildSharedFormatsQ() : buildFormatsQ(), {
    skip: !itemId,
    variables: { asset_Id: itemId, fmtType_Slug: PROXY_FORMAT_SLUG },
  });

  const proxyFormat = getFirstNestedWithSaneDefault<FormatData[]>(
    proxyFormatData as Record<string, FormatData[]> | null | undefined,
    [{ id: '' }],
  )[0];

  const { data: formatData } = useMetaQ<FormatMetaDataResponse>(
    {
      skip: !proxyFormat?.id,
      variables: { globalId: proxyFormat?.id, definitionSlugIn: [FD_MIMETYPE_SLUG] },
    },
    isShared,
  );

  return {
    assetMetaData,
    assetMetaDataLoading,
    assetMetaDataError,
    proxyFormatData,
    formatData,
    proxyFormat,
    refetchAssetMetaData,
  };
};

export const useMediaQueries = (itemId: string | undefined, isShared: boolean): UseMediaQueriesReturn => {
  return useQueries(itemId, isShared);
};
